import { useCallback, useEffect, useState } from "react";
import { MdAssessment, MdRefresh, MdSend } from "react-icons/md";
import { getApiErrorMessage } from "api";
import Button from "components/button";
import { PageContainer, PageHeader } from "components/page";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import { getAudience } from "components/assessment/audiences";
import AssessmentMetrics from "components/assessment/AssessmentMetrics";
import ArchetypeDistribution from "components/assessment/ArchetypeDistribution";
import TeamCompositionRadar from "components/assessment/TeamCompositionRadar";
import AssessmentTimeline from "components/assessment/AssessmentTimeline";
import StrengthsMatrix from "components/assessment/StrengthsMatrix";
import PeopleDirectory from "components/assessment/PeopleDirectory";
import TeamBalanceInsights from "components/assessment/TeamBalanceInsights";
import SendAssessmentModal from "components/assessment/SendAssessmentModal";
import { track } from "lib/mixpanel";

const LoadingSkeleton = () => (
  <>
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} className="h-28 w-full rounded-card" />
      ))}
    </div>
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <Skeleton className="h-96 w-full rounded-card" />
      <Skeleton className="h-96 w-full rounded-card" />
    </div>
    <Skeleton className="h-72 w-full rounded-card" />
  </>
);

export default function AssessmentOverview({ audience }) {
  const config = getAudience(audience);
  const { service, labels } = config;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await service.getAssessmentOverview();
      if (response.success) {
        setData(response.data);
      } else {
        setError(response);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => {
    track(config.events.assessmentViewed);
    fetchDashboard();
  }, [config, fetchDashboard]);

  const hasAssessments =
    Boolean(data?.metrics?.assessmentsCompleted) || (data?.staffDirectory?.length ?? 0) > 0;

  const renderContent = () => {
    if (error) {
      return (
        <ErrorState
          message={getApiErrorMessage(error, "Could not load assessment data")}
          onRetry={fetchDashboard}
        />
      );
    }
    if (!data) return <LoadingSkeleton />;
    return (
      <>
        <AssessmentMetrics metrics={data.metrics} config={config} />

        {hasAssessments ? (
          <>
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <ArchetypeDistribution distribution={data.archetypeDistribution} />
              <TeamCompositionRadar strengthsMatrix={data.strengthsMatrix} />
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <AssessmentTimeline timeline={data.timeline} />
              <StrengthsMatrix strengthsMatrix={data.strengthsMatrix} />
            </div>

            <PeopleDirectory people={data.staffDirectory} config={config} />

            <TeamBalanceInsights teamBalance={data.teamBalance} config={config} />
          </>
        ) : (
          <EmptyState
            icon={MdAssessment}
            title="No Assessments Yet"
            description={`Send the ministry archetype assessment to your ${labels.pluralLower} to see team insights here.`}
            action={
              <Button leftIcon={<MdSend />} onClick={() => setShowSendModal(true)}>
                Send Assessment
              </Button>
            }
          />
        )}
      </>
    );
  };

  return (
    <PageContainer>
      <PageHeader
        title="Assessment"
        description={`Ministry archetype insights for your ${labels.pluralLower}`}
        actions={
          <>
            <Button
              variant="secondary"
              leftIcon={<MdRefresh />}
              loading={loading && Boolean(data)}
              disabled={loading}
              onClick={fetchDashboard}
            >
              Refresh
            </Button>
            <Button leftIcon={<MdSend />} onClick={() => setShowSendModal(true)}>
              Send Assessment
            </Button>
          </>
        }
      />

      {renderContent()}

      <SendAssessmentModal
        isOpen={showSendModal}
        config={config}
        onClose={() => {
          setShowSendModal(false);
          fetchDashboard();
        }}
      />
    </PageContainer>
  );
}
